import { useEffect, useState, useCallback } from "react";
import api from "../../api/api";
import { toast } from "react-toastify";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
} from "recharts";
import { HiMiniAdjustmentsVertical } from "react-icons/hi2";

const MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

const TIPI = [
  { key: "ordinari",      label: "Ordinari",        color: "#3b82f6" },
  { key: "lav_chiamata",  label: "Lav. a chiamata", color: "#f59e0b" },
  { key: "somministrati", label: "Somministrati",    color: "#8b5cf6" },
  { key: "sportivi",      label: "Sportivi",         color: "#10b981" },
  { key: "distaccati",    label: "Distaccati",       color: "#06b6d4" },
];

const MODULI = [
  { key: "kpi",         label: "KPI"                    },
  { key: "andamento",   label: "Andamento mensile"       },
  { key: "ripartizione",label: "Ripartizione per tipo"   },
  { key: "referenti",   label: "Per referente"           },
];

const LS_KEY = "cedolini_dashboard_moduli";

const defaultModuli = () => {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return { kpi: true, andamento: true, ripartizione: true, referenti: true };
};

const meseLabel = (anno, mese) => `${MESI[mese - 1]} ${anno}`;
const fmt  = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });
const fmtK = (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(Math.round(v));

const DeltaBadge = ({ a, b }) => {
  if (!b || b === 0) return null;
  const val = ((a - b) / b * 100).toFixed(1);
  const pos = Number(val) >= 0;
  return (
    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${pos ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
      {pos ? "+" : ""}{val}%
    </span>
  );
};

const BarTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const tot = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-white border border-gray-200 rounded shadow-md p-2 text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p) => p.value > 0 && (
        <p key={p.dataKey} style={{ color: p.fill }}>{p.name}: {fmt(p.value)}</p>
      ))}
      <p className="font-semibold mt-1 border-t pt-1">Totale: {fmt(tot)}</p>
    </div>
  );
};

const PieTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-white border border-gray-200 rounded shadow-md p-2 text-xs">
      <p style={{ color: p.payload.color }} className="font-semibold">{p.name}</p>
      <p>{fmt(p.value)} ({(p.payload.pct * 100).toFixed(1)}%)</p>
    </div>
  );
};

// "Ordinari" = tutto ciò che non è una categoria speciale (liq_mensile e liq_sep esclusi:
// non sono sottoinsiemi puliti del totale Zucchetti e causerebbero doppio conteggio)
const addOrdinari = (r) => {
  const speciali = ["lav_chiamata","somministrati","sportivi","distaccati"]
    .reduce((s, k) => s + Number(r[k] || 0), 0);
  return { ...r, ordinari: Math.max(0, Number(r.totali || 0) - speciali) };
};

export const CedoliniDashboard = () => {
  const [mensile, setMensile]       = useState([]);
  const [perRef, setPerRef]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selIdx, setSelIdx]         = useState(0);
  const [loadingRef, setLoadingRef] = useState(false);
  const [moduli, setModuli]         = useState(defaultModuli);
  const [showCfg, setShowCfg]       = useState(false);

  useEffect(() => {
    api.get("/cedolini/dashboard")
      .then((r) => {
        const rows = (r.data.mensile || []).map(addOrdinari);
        const oggi = new Date();
        const currentIdx = rows.findIndex((row) => (
          Number(row.anno) === oggi.getFullYear() && Number(row.mese) === oggi.getMonth() + 1
        ));
        setMensile(rows);
        setSelIdx(currentIdx >= 0 ? currentIdx : 0);
        setPerRef(r.data.per_referente || []);
      })
      .catch(() => toast.error("Errore caricamento cedolini"))
      .finally(() => setLoading(false));
  }, []);

  const caricaReferenti = useCallback((anno, mese) => {
    setLoadingRef(true);
    api.get(`/cedolini/dashboard/referenti?anno=${anno}&mese=${mese}`)
      .then((r) => setPerRef(r.data))
      .catch(() => toast.error("Errore caricamento referenti"))
      .finally(() => setLoadingRef(false));
  }, []);

  const handleSelIdx = (idx) => {
    setSelIdx(idx);
    const d = mensile[idx];
    if (d) caricaReferenti(d.anno, d.mese);
  };

  const toggleModulo = (key) => {
    setModuli((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      return next;
    });
  };

  if (loading) return (
    <div className="col-span-2 el-container">
      <p className="text-sm text-gray-400 p-4">Caricamento cedolini...</p>
    </div>
  );
  if (!mensile.length) return null;

  const sel     = mensile[selIdx];
  const selPrec = mensile[selIdx + 1];
  const totSel  = Number(sel.totali || 0);

  const barData = [...mensile].reverse().map((r, i) => ({
    name:          meseLabel(r.anno, r.mese),
    ordinari:      Number(r.ordinari      || 0),
    lav_chiamata:  Number(r.lav_chiamata  || 0),
    somministrati: Number(r.somministrati || 0),
    sportivi:      Number(r.sportivi      || 0),
    distaccati:    Number(r.distaccati    || 0),
    _idx:          mensile.length - 1 - i,
  }));

  const pieData = TIPI.map((t) => ({
    name:  t.label,
    value: Number(sel[t.key] || 0),
    color: t.color,
    pct:   totSel > 0 ? Number(sel[t.key] || 0) / totSel : 0,
  })).filter((d) => d.value > 0);

  const topRef = perRef.slice(0, 10);
  const refMax = topRef.length ? Number(topRef[0].cedolini) : 1;

  const nAttivi = Object.values(moduli).filter(Boolean).length;

  return (
    <div className="col-span-2">
      <div className="el-container">

        {/* header */}
        <div className="flex items-center justify-between px-3 pt-2 pb-2 border-b border-gray-100 mb-3">
          <h4 className="text-sm font-semibold text-gray-700">Cedolini elaborati</h4>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Periodo:</span>
              <select
                className="text-xs border border-gray-200 rounded px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
                value={selIdx}
                onChange={(e) => handleSelIdx(Number(e.target.value))}
              >
                {mensile.map((d, i) => (
                  <option key={i} value={i}>{meseLabel(d.anno, d.mese)}</option>
                ))}
              </select>
            </div>

            {/* bottone configurazione moduli */}
            <div className="relative">
              <button
                onClick={() => setShowCfg((v) => !v)}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${
                  showCfg ? "bg-blue-50 border-blue-300 text-blue-700" : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                <HiMiniAdjustmentsVertical className="w-3.5 h-3.5" />
                Moduli {nAttivi < MODULI.length && <span className="text-blue-500">({nAttivi}/{MODULI.length})</span>}
              </button>

              {showCfg && (
                <div className="absolute right-0 top-8 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[180px]">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pannelli visibili</p>
                  {MODULI.map((m) => (
                    <label key={m.key} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 rounded px-1">
                      <input
                        type="checkbox"
                        checked={!!moduli[m.key]}
                        onChange={() => toggleModulo(m.key)}
                        className="cursor-pointer accent-blue-500"
                      />
                      <span className="text-xs text-gray-700">{m.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* KPI */}
        {moduli.kpi && (
          <div className="grid grid-cols-3 gap-3 px-3 mb-4">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-xs text-blue-500 font-medium uppercase tracking-wide mb-1">Cedolini totali</p>
              <p className="text-2xl font-bold text-blue-700">{fmt(sel.totali)}</p>
              <div className="mt-1">
                <DeltaBadge a={totSel} b={Number(selPrec?.totali || 0)} />
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-xs text-green-500 font-medium uppercase tracking-wide mb-1">Aziende attive</p>
              <p className="text-2xl font-bold text-green-700">{fmt(sel.n_aziende)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Media per azienda</p>
              <p className="text-2xl font-bold text-gray-700">
                {sel.n_aziende > 0 ? (totSel / Number(sel.n_aziende)).toFixed(1) : "—"}
              </p>
            </div>
          </div>
        )}

        {/* Andamento + donut */}
        {(moduli.andamento || moduli.ripartizione) && (
          <div className={`grid grid-cols-1 gap-4 px-3 mb-4 ${moduli.andamento && moduli.ripartizione ? "md:grid-cols-3" : ""}`}>

            {moduli.andamento && (
              <div className={moduli.ripartizione ? "md:col-span-2" : "col-span-1"}>
                <p className="text-xs font-medium text-gray-500 mb-2">Andamento mensile</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={barData} barSize={22} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={fmtK} tick={{ fontSize: 10 }} width={36} />
                    <Tooltip content={<BarTip />} />
                    {TIPI.map((t) => (
                      <Bar key={t.key} dataKey={t.key} name={t.label} stackId="a" fill={t.color}
                        radius={t.key === "liq_sep" ? [3, 3, 0, 0] : [0, 0, 0, 0]}>
                        {barData.map((entry) => (
                          <Cell key={entry.name} fill={t.color} opacity={entry._idx === selIdx ? 1 : 0.4} />
                        ))}
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 justify-center">
                  {TIPI.map((t) => (
                    <span key={t.key} className="flex items-center gap-1 text-xs text-gray-500">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: t.color }} />
                      {t.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {moduli.ripartizione && (
              <div className="flex flex-col">
                <p className="text-xs font-medium text-gray-500 mb-2">Ripartizione {meseLabel(sel.anno, sel.mese)}</p>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={72}
                      dataKey="value" startAngle={90} endAngle={-270} paddingAngle={2}
                      label={false} labelLine={false}>
                      {pieData.map((e) => <Cell key={e.name} fill={e.color} />)}
                    </Pie>
                    <Tooltip content={<PieTip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col gap-1 mt-1">
                  {pieData.map((d) => (
                    <div key={d.name} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                        <span className="text-gray-600">{d.name}</span>
                      </span>
                      <span className="font-medium text-gray-700">
                        {fmt(d.value)} <span className="text-gray-400 font-normal">({(d.pct * 100).toFixed(0)}%)</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Per referente */}
        {moduli.referenti && (
          <div className="px-3 pb-4">
            <p className="text-xs font-medium text-gray-500 mb-2">
              Per referente — {meseLabel(sel.anno, sel.mese)}
              {loadingRef && <span className="ml-2 text-gray-300">aggiornamento...</span>}
            </p>
            <ResponsiveContainer width="100%" height={topRef.length * 32 + 20}>
              <BarChart
                data={topRef.map((r) => ({ name: r.label, cedolini: Number(r.cedolini), aziende: Number(r.aziende) }))}
                layout="vertical"
                margin={{ top: 0, right: 48, left: 8, bottom: 0 }}
                barSize={14}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmtK} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip
                  formatter={(v, name) => [fmt(v), name === "cedolini" ? "Cedolini" : "Aziende"]}
                  contentStyle={{ fontSize: 11 }}
                />
                <Bar dataKey="cedolini" name="Cedolini" fill="#3b82f6" radius={[0, 3, 3, 0]}
                  label={{ position: "right", fontSize: 10, fill: "#6b7280", formatter: (v) => fmt(v) }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {nAttivi === 0 && (
          <p className="text-xs text-gray-400 text-center py-6">
            Nessun pannello attivo — usa <HiMiniAdjustmentsVertical className="inline w-3 h-3" /> Moduli per abilitarne qualcuno.
          </p>
        )}

      </div>
    </div>
  );
};
